import * as React from 'react'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import { TaskType } from '@/app/constants/taskType'
import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import usePrintService from '@/modules/drivers/services/PrintService'
import { usePrintJobGuard } from './usePrintJobGuard'
import { supportsSignatures } from '../utils/reportDefaultParams'

// Report types that are exported as CSV instead of the default PDF.
export const CSV_REPORT_TYPES = new Set<ReportType>([
  ReportType.TIMESHEET_PAYMENT_DETAILS_CSV,
])

const DATE_RE = /^\d{4}-\d{2}-\d{2}/

interface GeneratePrintParams {
  companyId: string
  driverIds: Array<number | string>
  dateFrom: string
  dateTo: string
  selectedReports: ReportType[]
  generateToSeparateFiles: boolean
  rememberSelected: boolean
  issuerSignature?: boolean
  driverSignature?: boolean
  outputType?: OutputType
}

export const useGeneratePrintout = () => {
  const { t } = useLocalize()
  const { addTask } = useTask()
  const { generatePrintoutForDateRange } = usePrintService()
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()

  const generatePrintouts = React.useCallback(
    async ({
      companyId,
      driverIds,
      dateFrom,
      dateTo,
      selectedReports,
      generateToSeparateFiles,
      rememberSelected,
      issuerSignature,
      driverSignature,
      outputType,
    }: GeneratePrintParams) => {
      try {
        if (!companyId || !DATE_RE.test(dateFrom) || !DATE_RE.test(dateTo)) {
          toast.error({
            text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
          })
          return
        }

        const drivers = driverIds.map((id) => String(id))

        for (const reportType of selectedReports) {
          try {
            const result = await generatePrintoutForDateRange({
              companyId,
              reportTypes: [reportType],
              driverIds: drivers,
              dateFrom,
              dateTo,
              generateToSeparateFiles,
              rememberSelected,
              issuerSignature: issuerSignature && supportsSignatures(reportType),
              driverSignature: driverSignature && supportsSignatures(reportType),
              outputType: CSV_REPORT_TYPES.has(reportType)
                ? OutputType.Csv
                : outputType,
            })

            if (result.taskId) {
              addTask(result.taskId, TaskType.Printout, reportType)
            }
          } catch (error) {
            console.error(`Error generating printout for ${reportType}:`, error)

            toast.error({
              title: t('printout.generation-error'),
              text: error instanceof Error ? error.message : String(error),
            })
          }
        }

       
      } catch (error) {
        console.error('Error generating printout:', error)

        toast.error({
          title: t('printout.generation-error'),
          text: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [generatePrintoutForDateRange, addTask, t],
  )

  const handleGeneratePrintout = React.useCallback(
    (params: GeneratePrintParams) =>
      runPrintJob(() => generatePrintouts(params)),
    [generatePrintouts, runPrintJob],
  )

  return {
    handleGeneratePrintout,
    isGenerating,
    busyTooltip,
  }
}
